import type { Id, ISODate } from '@contracts/common';
import { givenName } from '@contracts/assistant';
import { SHARING_MODES, type SharingMode } from '@contracts/sharing';
import { TASK_STATUS_LABEL, type Priority, type Recipient, type RecipientInput, type StrategyLink, type TaskStatus } from '@contracts/tasks';
import type {
  AssistantPresence, BoardDTO, BoardItem, BoardTaskCard, DoneRoot, ItemCreateBody, ItemMoveBody, ItemPatchBody, ItemPathEntry,
  ItemProjectRef,
} from '@contracts/workplace';
import { addDays } from '@domain/dates';
import { normalizeSearch } from '@domain/search';
import { SOON_DAYS } from '@domain/task-lists';
import { actorLabel, type AssistedSegment } from '../../lib/actor';
import { collapsedKey } from '../../lib/person-storage';
import {
  comparePositions, effectiveProject, positionBetween, validatePlacement, type TreeViolation,
} from '@domain/workplace-tree';
import { ApiClientError } from '../../api/client';

/**
 * The browser's projections of one board (spec §4.5, §6.2–§6.8). Every function here is pure: it reads the board
 * it is given and the board's `day`, never the browser's clock, so the three views, the Rail and the filters all
 * agree with each other and with the server on what is late, soon or due today.
 */

// Forest ----------------------------------------------------------------------------------------------------

export type ForestNode = {
  item: BoardItem;
  parent: ForestNode | null;
  children: ForestNode[];
  depth: number;
  rootId: Id;
  /** Spec §4.5 `x/y`: direct child tasks done over all direct child tasks; notes never count. */
  progress: { done: number; total: number };
};

export type Forest = {
  /** Big jobs first, then loose tasks (spec §6.2), each group in sibling order. */
  roots: ForestNode[];
  bigRoots: ForestNode[];
  looseRoots: ForestNode[];
  /** Every card in pre-order over `roots`. */
  order: BoardItem[];
  byId: Map<Id, ForestNode>;
};

const isOpenTask = (item: BoardItem): item is BoardTaskCard => item.itemType === 'task' && item.status !== 'completed';

/**
 * Builds the forest from a flat card list in any order. `parentId` is read once per card and every pass is linear
 * apart from the sibling sorts, so 2000 cards cost O(n log n). A card whose parent is absent (a stale optimistic
 * cache) stays visible at the top level rather than vanishing; cyclic data cannot loop.
 */
export function buildForest(items: readonly BoardItem[]): Forest {
  const byId = new Map<Id, ForestNode>();
  const parentOf: (Id | null)[] = [];
  for (const item of items) {
    parentOf.push(item.parentId);
    byId.set(item.id, { item, parent: null, children: [], depth: 0, rootId: item.id, progress: { done: 0, total: 0 } });
  }
  const tops: ForestNode[] = [];
  items.forEach((item, index) => {
    const node = byId.get(item.id)!;
    const parent = parentOf[index] === null ? undefined : byId.get(parentOf[index]!);
    if (parent === undefined || parent === node) { tops.push(node); return; }
    node.parent = parent;
    parent.children.push(node);
    if (item.itemType === 'task') {
      parent.progress.total += 1;
      if (item.status === 'completed') parent.progress.done += 1;
    }
  });
  const byPlace = (a: ForestNode, b: ForestNode): number => comparePositions(a.item, b.item);
  for (const node of byId.values()) if (node.children.length > 1) node.children.sort(byPlace);
  tops.sort(byPlace);

  const order: BoardItem[] = [];
  const visited = new Set<Id>();
  const walk = (top: ForestNode): void => {
    top.parent = null;
    const stack: [ForestNode, number][] = [[top, 0]];
    while (stack.length > 0) {
      const [node, depth] = stack.pop()!;
      if (visited.has(node.item.id)) continue;
      visited.add(node.item.id);
      node.depth = depth;
      node.rootId = top.item.id;
      order.push(node.item);
      for (let i = node.children.length - 1; i >= 0; i -= 1) stack.push([node.children[i]!, depth + 1]);
    }
  };
  const bigRoots = tops.filter((n) => n.children.length > 0);
  const looseRoots = tops.filter((n) => n.children.length === 0);
  for (const top of bigRoots) walk(top);
  for (const top of looseRoots) walk(top);
  // Corrupt cyclic data leaves cards no top reaches; they surface as tops instead of disappearing.
  for (const node of byId.values()) {
    if (visited.has(node.item.id)) continue;
    node.parent?.children.splice(node.parent.children.indexOf(node), 1);
    looseRoots.push(node);
    walk(node);
  }
  return { roots: [...bigRoots, ...looseRoots], bigRoots, looseRoots, order, byId };
}

export type TreeCounts = { cards: number; blocked: number; late: number };

/** The Rail's numbers per root (spec §4.5): live cards incl. notes, open blocked tasks, open tasks due before `day`. */
export function treeCounts(forest: Forest, day: ISODate): Map<Id, TreeCounts> {
  const out = new Map<Id, TreeCounts>();
  for (const root of forest.roots) out.set(root.item.id, { cards: 0, blocked: 0, late: 0 });
  for (const item of forest.order) {
    const counts = out.get(forest.byId.get(item.id)!.rootId)!;
    counts.cards += 1;
    if (isOpenTask(item) && item.blocked) counts.blocked += 1;
    if (isOpenTask(item) && item.deadline < day) counts.late += 1;
  }
  return out;
}

// Filters ---------------------------------------------------------------------------------------------------

export const FILTER_FLAGS = ['mine', 'blocked', 'late', 'soon', 'week', 'today', 'important', 'changed'] as const;
export type FilterFlag = typeof FILTER_FLAGS[number];
/** `user:<id>`: a task is always given to an internal user (spec §6.1, projects spec §4.1). */
export type RecipientFilter = `user:${string}`;
/**
 * `prj`: one project on the board (projects & stakeholders spec Màn 7): the project a card belongs to, its
 * own or the one it follows from a card above (`effectiveProjectOf`, PO rule 2026-09-27).
 */
/**
 * `share`: one sharing mode (sharing spec §9, D5), the mode of the card's home; the chip "Tất cả" is `null`.
 */
export type WorkplaceFilters = {
  q: string; f: FilterFlag[]; rcpt: RecipientFilter | null; prj: Id | null; share: SharingMode | null;
};
/**
 * What a filter needs besides itself: the board's day, who "Của tôi" is, the collapsed branches, and the cards the
 * activity log says changed in the last 24 hours ("Thay đổi 24h", UX wave D P1 — the board carries no updated-at).
 */
export type FilterContext = { day: ISODate; userId: Id; collapsed?: ReadonlySet<Id>; changed?: ReadonlySet<Id> };

/** The Sunday that closes `day`'s week (Monday–Sunday); a Sunday is its own. "Tuần này" runs through it (PO ruling). */
export function weekEnd(day: ISODate): ISODate {
  const weekday = new Date(`${day}T00:00:00Z`).getUTCDay();
  return addDays(day, (7 - weekday) % 7);
}

/** Case- and accent-insensitive search text, shared with the server's `fold_search` (`@domain/search`). */
export { normalizeSearch };

export function isFiltering(filters: WorkplaceFilters): boolean {
  return normalizeSearch(filters.q) !== '' || filters.f.length > 0 || filters.rcpt !== null || filters.prj !== null
    || filters.share !== null;
}

/** Reads `q`, `f`, `rcpt`, `prj` and `share` from the URL; unknown flags, recipient forms and modes are dropped (spec §6.1). */
export function parseFilters(params: URLSearchParams): WorkplaceFilters {
  const f = (params.get('f') ?? '').split(',').map((s) => s.trim())
    .filter((s, i, all): s is FilterFlag => (FILTER_FLAGS as readonly string[]).includes(s) && all.indexOf(s) === i);
  const raw = params.get('rcpt');
  const rcpt = raw !== null && /^user:[^:\s]+$/.test(raw) ? raw as RecipientFilter : null;
  const prj = params.get('prj');
  const mode = params.get('share');
  const share = SHARING_MODES.includes(mode as SharingMode) ? mode as SharingMode : null;
  return { q: params.get('q') ?? '', f, rcpt, prj: prj === null || prj === '' ? null : prj, share };
}

/** One identity per distinct filter, independent of flag order and of case/accents in the query. */
export function filtersKey(filters: WorkplaceFilters): string {
  return JSON.stringify([normalizeSearch(filters.q), [...new Set(filters.f)].sort(), filters.rcpt, filters.prj, filters.share]);
}

/** The BOD a task's work belongs to for "Của tôi": its recipient. */
const ownerOf = (recipient: Recipient): Id => recipient.userId;

const recipientMatches = (recipient: Recipient, rcpt: RecipientFilter): boolean => rcpt === `user:${recipient.userId}`;

type Ends = { soon: ISODate; week: ISODate };

/** A task chip. "Thay đổi 24h" is not one: it matches any card, notes included (see `filterForest`). */
function flagMatches(task: BoardTaskCard, flag: Exclude<FilterFlag, 'changed'>, ctx: FilterContext, ends: Ends): boolean {
  switch (flag) {
    case 'mine': return ownerOf(task.recipient) === ctx.userId;
    case 'blocked': return task.blocked;
    case 'late': return task.deadline < ctx.day;
    case 'soon': return ctx.day <= task.deadline && task.deadline <= ends.soon;
    // Due from today through Sunday; what is already late has its own chip.
    case 'week': return ctx.day <= task.deadline && task.deadline <= ends.week;
    case 'important': return task.priority === 'high' || task.priority === 'urgent';
    case 'today': return task.deadline <= ctx.day;
  }
}

const isTaskFlag = (flag: FilterFlag): flag is Exclude<FilterFlag, 'changed'> => flag !== 'changed';

export type VisibleRow = { item: BoardItem; depth: number; dimmed: boolean; hasChildren: boolean };
export type VisibleForest = { active: boolean; matchIds: Set<Id>; rows: VisibleRow[] };

/**
 * The project card `id` belongs to (PO 2026-09-28): its root's — `effectiveProject`, the rule the server applies too.
 * Only a root names one; every card below follows it.
 */
export function effectiveProjectOf(forest: Forest, id: Id): ItemProjectRef | null {
  return effectiveProject(id, (at) => forest.byId.get(at)?.item);
}

/** A tree's belonging (PO 2026-09-28): what its root carries — a project, a strategy target, or neither. */
export type TreeBelonging =
  | { kind: 'project'; project: ItemProjectRef }
  | { kind: 'strategy'; link: NonNullable<StrategyLink> }
  | { kind: 'independent' };

/**
 * The belonging of the tree holding card `id`, read from its root; null when the walk meets a card `lookup` does not
 * know (a tree only partly at hand), or loops.
 */
export function belongingOf(id: Id, lookup: (id: Id) => BoardItem | undefined): TreeBelonging | null {
  const seen = new Set<Id>();
  for (let at: Id = id; !seen.has(at);) {
    const item = lookup(at);
    if (item === undefined) return null;
    if (item.parentId === null) {
      if (item.project !== null) return { kind: 'project', project: item.project };
      return item.strategy ? { kind: 'strategy', link: item.strategy } : { kind: 'independent' };
    }
    seen.add(at);
    at = item.parentId;
  }
  return null;
}

const belongingKey = (belonging: TreeBelonging): string => (belonging.kind === 'project' ? `project:${belonging.project.id}`
  : belonging.kind === 'strategy' ? `${belonging.link.kind}:${belonging.link.id}` : 'independent');

/**
 * Two trees are one group (the server's `OTHER_BELONGING` otherwise): the same project, the same strategy target, or
 * both independent. An unknown belonging is no group.
 */
export function sameBelonging(a: TreeBelonging | null, b: TreeBelonging | null): boolean {
  return a !== null && b !== null && belongingKey(a) === belongingKey(b);
}

/** What a new root names: a project, a strategy link, or neither. */
export type RootLinks = { project: ItemProjectRef | null; strategyLink: StrategyLink };

/**
 * What a new root made from the root of `source` names (PO 2026-09-28: the belonging is chosen when the work is
 * created): that tree's project, its strategy target, or neither. A tree not at hand leaves `fallback` — the project
 * of a filtered board, or none.
 */
export function rootLinksFrom(source: TreeBelonging | null, fallback: ItemProjectRef | null): RootLinks {
  if (source === null) return { project: fallback, strategyLink: null };
  if (source.kind === 'project') return { project: source.project, strategyLink: null };
  return { project: null, strategyLink: source.kind === 'strategy' ? source.link : null };
}

/** `links` as the fields of a create request: only those it names. */
export const rootLinkFields = (links: RootLinks): { projectId?: Id; strategyLink?: NonNullable<StrategyLink> } => ({
  ...(links.project === null ? {} : { projectId: links.project.id }),
  ...(links.strategyLink === null ? {} : { strategyLink: links.strategyLink }),
});

/**
 * A project's "Công việc" tab: the board's cards that belong to the project — their own, or followed from a card above
 * (`effectiveProject`) — and nothing else: no parent outside the project as context, no child that names another
 * project. A card whose parent is left out (it names the project under a card that is not the project's) stands as a
 * root (`buildForest`). Read from the whole board, so a card follows a card above it whatever is shown.
 */
export function projectCards(items: readonly BoardItem[], project: Id): BoardItem[] {
  const byId = new Map(items.map((item) => [item.id, item]));
  return items.filter((item) => effectiveProject(item.id, (at) => byId.get(at))?.id === project);
}

/**
 * Spec §6.3. Without a filter: every card, collapsed branches hidden. With one: the cards that match plus their
 * path to the root (dimmed as context), collapsed branches ignored. Chips and `rcpt` are AND-combined and match open
 * tasks only; the text query, `prj` and `share` match every card, notes included. `retainedIds` — cards just created or
 * edited under this filter — stay on screen, undimmed, with their path.
 */
export function filterForest(
  forest: Forest, filters: WorkplaceFilters, retainedIds: ReadonlySet<Id>, ctx: FilterContext,
): VisibleForest {
  const active = isFiltering(filters);
  const matchIds = new Set<Id>();
  const rows: VisibleRow[] = [];
  if (!active) {
    const collapsed = ctx.collapsed ?? new Set<Id>();
    const hidden = new Set<Id>();
    for (const item of forest.order) {
      const node = forest.byId.get(item.id)!;
      if (node.parent !== null && (hidden.has(node.parent.item.id) || collapsed.has(node.parent.item.id))) {
        hidden.add(item.id);
        continue;
      }
      rows.push({ item, depth: node.depth, dimmed: false, hasChildren: node.children.length > 0 });
    }
    return { active, matchIds, rows };
  }

  const q = normalizeSearch(filters.q);
  const taskFlags = filters.f.filter(isTaskFlag);
  const chips = taskFlags.length > 0 || filters.rcpt !== null;
  const changedOnly = filters.f.includes('changed');
  const changed = ctx.changed ?? new Set<Id>();
  const ends: Ends = { soon: addDays(ctx.day, SOON_DAYS), week: weekEnd(ctx.day) };
  const shown = new Set<Id>();
  const context = new Set<Id>();
  const keep = (node: ForestNode): void => {
    shown.add(node.item.id);
    for (let up = node.parent; up !== null && !shown.has(up.item.id) && !context.has(up.item.id); up = up.parent) {
      context.add(up.item.id);
    }
  };
  for (const item of forest.order) {
    const node = forest.byId.get(item.id)!;
    let hit = q === '' || normalizeSearch(item.title).includes(q);
    if (hit && changedOnly) hit = changed.has(item.id);
    if (hit && filters.prj !== null) hit = effectiveProjectOf(forest, item.id)?.id === filters.prj;
    if (hit && filters.share !== null) hit = item.sharing.mode === filters.share;
    if (hit && chips) {
      hit = isOpenTask(item)
        && taskFlags.every((flag) => flagMatches(item, flag, ctx, ends))
        && (filters.rcpt === null || recipientMatches(item.recipient, filters.rcpt));
    }
    if (hit) { matchIds.add(item.id); keep(node); } else if (retainedIds.has(item.id)) keep(node);
  }
  for (const item of forest.order) {
    const isShown = shown.has(item.id);
    if (!isShown && !context.has(item.id)) continue;
    const node = forest.byId.get(item.id)!;
    rows.push({ item, depth: node.depth, dimmed: !isShown, hasChildren: node.children.length > 0 });
  }
  return { active, matchIds, rows };
}

/** Cards kept on screen because they were just created or edited under the current filter (spec §6.3). */
export type Retention = { key: string; ids: ReadonlySet<Id> };

export function retain(r: Retention, filters: WorkplaceFilters, id: Id): Retention {
  const key = filtersKey(filters);
  return { key, ids: new Set(r.key === key ? [...r.ids, id] : [id]) };
}

/** The retained ids for this filter; a changed filter drops them all. */
export function retainedFor(r: Retention, filters: WorkplaceFilters): ReadonlySet<Id> {
  return r.key === filtersKey(filters) ? r.ids : new Set<Id>();
}

// Kanban ----------------------------------------------------------------------------------------------------

export type KanbanCard = {
  id: Id; title: string; status: TaskStatus; recipient: Recipient; deadline: ISODate; priority: Priority;
  blocked: boolean;
  /** The board card, or null for a completed root that only `recentDone` carries. */
  card: BoardTaskCard | null;
  /** Ancestors from the root down to the parent; empty on a root. */
  path: ItemPathEntry[];
  /** A root's label: "Việc lớn · N thẻ" or "Việc lẻ"; null below a root. */
  root: { kind: 'big'; cards: number } | { kind: 'loose' } | null;
};

export type KanbanColumns = Record<TaskStatus, KanbanCard[]> & { counts: Record<TaskStatus, number> };

const priorityRank: Record<Priority, number> = { urgent: 0, high: 1, normal: 2, low: 3 };

/**
 * Spec §6.2 Bảng: tasks at every depth, never notes, in three status columns. Each column runs blocked first,
 * then overdue on `day`, then nearest deadline, then priority. "Đã xong" holds completed tasks of running trees
 * plus `recentDone`; a root present in both (a stale optimistic cache) is shown once, from the board row.
 */
export function kanbanColumns(items: readonly BoardItem[], recentDone: readonly DoneRoot[], day: ISODate): KanbanColumns {
  const forest = buildForest(items);
  const size = new Map<Id, number>();
  for (let i = forest.order.length - 1; i >= 0; i -= 1) {
    const node = forest.byId.get(forest.order[i]!.id)!;
    size.set(node.item.id, 1 + node.children.reduce((sum, c) => sum + size.get(c.item.id)!, 0));
  }
  const paths = new Map<Id, ItemPathEntry[]>();
  const columns: Record<TaskStatus, KanbanCard[]> = { not_started: [], in_progress: [], completed: [] };
  for (const item of forest.order) {
    const node = forest.byId.get(item.id)!;
    const path = node.parent === null ? [] : [...paths.get(node.parent.item.id)!, { id: node.parent.item.id, title: node.parent.item.title }];
    paths.set(item.id, path);
    if (item.itemType !== 'task') continue;
    const cards = size.get(item.id)!;
    columns[item.status].push({
      id: item.id, title: item.title, status: item.status, recipient: item.recipient, deadline: item.deadline,
      priority: item.priority, blocked: item.blocked && item.status !== 'completed', card: item, path,
      root: node.parent !== null ? null : cards > 1 ? { kind: 'big', cards } : { kind: 'loose' },
    });
  }
  for (const done of recentDone) {
    if (forest.byId.has(done.id)) continue;
    columns.completed.push({
      id: done.id, title: done.title, status: 'completed', recipient: done.recipient, deadline: done.deadline,
      priority: done.priority, blocked: false, card: null, path: [],
      root: done.cardCount > 1 ? { kind: 'big', cards: done.cardCount } : { kind: 'loose' },
    });
  }
  const late = (c: KanbanCard): boolean => c.status !== 'completed' && c.deadline < day;
  const compare = (a: KanbanCard, b: KanbanCard): number => Number(b.blocked) - Number(a.blocked)
    || Number(late(b)) - Number(late(a)) || a.deadline.localeCompare(b.deadline)
    || priorityRank[a.priority] - priorityRank[b.priority] || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
  for (const column of Object.values(columns)) column.sort(compare);
  return {
    ...columns,
    counts: { not_started: columns.not_started.length, in_progress: columns.in_progress.length, completed: columns.completed.length },
  };
}

// Sort, group and lanes (UX wave C, 2026-09-25) ------------------------------------------------------------

/**
 * The List's sortable columns. "Vướng từ" (`blockedSince`) sorts by the instant the spell began, so ascending puts
 * the card blocked longest first; a card with no known start sorts with the unblocked ones, last.
 */
export const LIST_SORT_KEYS = ['title', 'status', 'priority', 'recipient', 'deadline', 'blocker', 'blockedSince'] as const;
export type ListSortKey = typeof LIST_SORT_KEYS[number];
export type ListSort = { key: ListSortKey; dir: 'asc' | 'desc' };
export const LIST_GROUPS = ['none', 'recipient', 'root', 'status'] as const;
export type ListGroup = typeof LIST_GROUPS[number];
export const BOARD_LANES = ['none', 'recipient', 'root'] as const;
export type BoardLane = typeof BOARD_LANES[number];
/** How the List and the Board lay the same cards out; in the URL beside the filters, so a link keeps it. */
export type Arrangement = { sort: ListSort | null; group: ListGroup; lane: BoardLane };

const oneOf = <T extends string>(all: readonly T[], raw: string | null, fallback: T): T =>
  raw !== null && (all as readonly string[]).includes(raw) ? raw as T : fallback;

/** Reads `sort` (`deadline`, or `-deadline` descending), `group` and `lane`; anything unknown is the default. */
export function parseArrangement(params: URLSearchParams): Arrangement {
  const raw = params.get('sort') ?? '';
  const desc = raw.startsWith('-');
  const key = desc ? raw.slice(1) : raw;
  const sort = (LIST_SORT_KEYS as readonly string[]).includes(key) ? { key: key as ListSortKey, dir: desc ? 'desc' as const : 'asc' as const } : null;
  return { sort, group: oneOf(LIST_GROUPS, params.get('group'), 'none'), lane: oneOf(BOARD_LANES, params.get('lane'), 'none') };
}

/** Writes an arrangement into `next`; a default value leaves its key out of the URL. */
export function writeArrangement(next: URLSearchParams, arrangement: Arrangement): void {
  const { sort, group, lane } = arrangement;
  if (sort === null) next.delete('sort'); else next.set('sort', `${sort.dir === 'desc' ? '-' : ''}${sort.key}`);
  if (group === 'none') next.delete('group'); else next.set('group', group);
  if (lane === 'none') next.delete('lane'); else next.set('lane', lane);
}

/** A header click: ascending, then descending, then back to the tree's own order. */
export function nextSort(current: ListSort | null, key: ListSortKey): ListSort | null {
  if (current?.key !== key) return { key, dir: 'asc' };
  return current.dir === 'asc' ? { key, dir: 'desc' } : null;
}

/** Whether the List shows the tree (indented, foldable) or a flat table (sorted or grouped). */
export const isFlatList = (arrangement: Pick<Arrangement, 'sort' | 'group'>): boolean =>
  arrangement.sort !== null || arrangement.group !== 'none';

type NameOf = (task: BoardTaskCard) => string | undefined;
const statusRank: Record<TaskStatus, number> = { not_started: 0, in_progress: 1, completed: 2 };
const collator = new Intl.Collator('vi', { sensitivity: 'base', numeric: true });

/** A row's value in one column; `null` (a note, no name, nothing blocking) always sorts last, either way. */
function sortValue(item: BoardItem, key: ListSortKey, nameOf: NameOf): string | number | null {
  if (key === 'title') return item.title;
  if (item.itemType !== 'task') return null;
  switch (key) {
    case 'status': return statusRank[item.status];
    case 'priority': return priorityRank[item.priority];
    case 'recipient': return nameOf(item) ?? null;
    case 'deadline': return item.deadline;
    case 'blocker': return item.blocked && item.status !== 'completed' && item.blocker ? item.blocker : null;
    case 'blockedSince':
      return item.blocked && item.status !== 'completed' && typeof item.blockedSince === 'string' ? Date.parse(item.blockedSince) : null;
  }
}

export type ListSection = { key: string; label: string | null; rows: VisibleRow[] };

const NOTES = 'Ghi chú';
const NO_NAME = 'Chưa rõ người nhận';
const LOOSE = 'Việc lẻ';

/**
 * The List's rows as the person arranged them. With no sort and no group it is the tree, untouched, in one
 * section. Otherwise the table is flat: the dimmed path context of a filter is dropped (it is not a match), rows
 * are sorted within their group — ties, and an unsorted group, keep the tree's order — and each group is a
 * section with its heading. Priority ascends from "Khẩn cấp" (rank 1) down to "Thấp".
 */
export function arrangeList(
  rows: readonly VisibleRow[], arrangement: Pick<Arrangement, 'sort' | 'group'>, forest: Forest, nameOf: NameOf,
): ListSection[] {
  if (!isFlatList(arrangement)) return [{ key: 'tree', label: null, rows: [...rows] }];
  const { sort, group } = arrangement;
  const flat = rows.filter((row) => !row.dimmed);
  if (sort !== null) {
    const index = new Map(flat.map((row, i) => [row.item.id, i]));
    const sign = sort.dir === 'asc' ? 1 : -1;
    flat.sort((a, b) => {
      const va = sortValue(a.item, sort.key, nameOf);
      const vb = sortValue(b.item, sort.key, nameOf);
      if (va === null || vb === null) {
        if (va !== vb) return va === null ? 1 : -1;
      } else {
        const order = typeof va === 'number' && typeof vb === 'number' ? va - vb : collator.compare(String(va), String(vb));
        if (order !== 0) return order * sign;
      }
      return index.get(a.item.id)! - index.get(b.item.id)!;
    });
  }
  if (group === 'none') return [{ key: 'all', label: null, rows: flat }];

  const sections = new Map<string, ListSection>();
  const add = (key: string, label: string, row: VisibleRow) => {
    const section = sections.get(key) ?? { key, label, rows: [] };
    section.rows.push(row);
    sections.set(key, section);
  };
  for (const row of flat) {
    const { item } = row;
    if (group === 'root') {
      const rootId = forest.byId.get(item.id)?.rootId ?? item.id;
      const loose = forest.looseRoots.some((node) => node.item.id === rootId);
      add(loose ? 'loose' : rootId, loose ? LOOSE : forest.byId.get(rootId)?.item.title ?? item.title, row);
    } else if (item.itemType !== 'task') {
      add('notes', NOTES, row);
    } else if (group === 'status') {
      add(item.status, TASK_STATUS_LABEL[item.status], row);
    } else {
      const name = nameOf(item);
      add(name === undefined ? 'unknown' : `${item.recipient.kind}:${recipientId(item.recipient)}`, name ?? NO_NAME, row);
    }
  }
  const order = (section: ListSection): [number, string] => {
    if (section.key === 'notes') return [3, ''];
    if (group === 'status') return [statusRank[section.key as TaskStatus], ''];
    if (group === 'root') {
      if (section.key === 'loose') return [2, ''];
      return [1, String(forest.bigRoots.findIndex((node) => node.item.id === section.key)).padStart(8, '0')];
    }
    return section.key === 'unknown' ? [2, ''] : [1, section.label ?? ''];
  };
  return [...sections.values()].sort((a, b) => {
    const [ra, la] = order(a);
    const [rb, lb] = order(b);
    return ra - rb || (group === 'recipient' ? collator.compare(la, lb) : la.localeCompare(lb));
  });
}

const recipientId = (recipient: Recipient): Id => recipient.userId;

export type KanbanLane = { key: string; label: string | null; columns: Record<TaskStatus, KanbanCard[]> };

/**
 * The Board's horizontal lanes: none (one lane, the plain Board), one per recipient, or one per big job with the
 * loose work in "Việc lẻ" and trees finished lately (only `recentDone` knows them) in "Đã xong gần đây". Each
 * lane keeps the columns' own order, so blocked cards stay pinned at the top of every cell.
 */
export function kanbanLanes(
  columns: Record<TaskStatus, KanbanCard[]>, lane: BoardLane, forest: Forest, nameOf: (card: KanbanCard) => string | undefined,
): KanbanLane[] {
  if (lane === 'none') return [{ key: 'all', label: null, columns }];
  const lanes = new Map<string, KanbanLane & { rank: [number, string] }>();
  const bigIndex = new Map(forest.bigRoots.map((node, i) => [node.item.id, i]));
  for (const status of ['not_started', 'in_progress', 'completed'] as const) {
    for (const card of columns[status]) {
      let key: string;
      let label: string;
      let rank: [number, string];
      if (lane === 'recipient') {
        const name = nameOf(card);
        key = name === undefined ? 'unknown' : `${card.recipient.kind}:${recipientId(card.recipient)}`;
        label = name ?? NO_NAME;
        rank = name === undefined ? [2, ''] : [1, name];
      } else {
        const rootId = card.card === null ? null : forest.byId.get(card.id)?.rootId ?? card.id;
        const big = rootId === null ? undefined : bigIndex.get(rootId);
        if (rootId === null) { key = 'done'; label = 'Đã xong gần đây'; rank = [3, '']; }
        else if (big === undefined) { key = 'loose'; label = LOOSE; rank = [2, '']; }
        else { key = rootId; label = forest.byId.get(rootId)!.item.title; rank = [1, String(big).padStart(8, '0')]; }
      }
      const entry = lanes.get(key) ?? { key, label, rank, columns: { not_started: [], in_progress: [], completed: [] } };
      entry.columns[status].push(card);
      lanes.set(key, entry);
    }
  }
  return [...lanes.values()]
    .sort((a, b) => a.rank[0] - b.rank[0] || (lane === 'recipient' ? collator.compare(a.rank[1], b.rank[1]) : a.rank[1].localeCompare(b.rank[1])))
    .map(({ key, label, columns: cells }) => ({ key, label, columns: cells }));
}

// Undo ------------------------------------------------------------------------------------------------------

export type UndoableField =
  | 'title' | 'description' | 'recipient' | 'deadline' | 'priority' | 'blocked' | 'blocker' | 'strategyLink';

/** A board card being deleted, with what the board does not carry and whether comments or files hang on it. */
export type DeletedCard = BoardItem & {
  description?: string | null; strategyLink?: StrategyLink; commentCount: number; fileCount: number;
};

/**
 * One action of the person's own. `version` is the version the save returned (the inverse PATCH must carry it);
 * `previous` supplies old values the board card does not hold, such as the description.
 */
export type UndoAction =
  | { kind: 'create'; id: Id; version: number }
  | { kind: 'move'; id: Id }
  | { kind: 'field'; id: Id; version: number; fields: UndoableField[]; previous?: Partial<Record<UndoableField, unknown>> }
  | { kind: 'status'; id: Id; version: number }
  | { kind: 'type'; id: Id; version: number }
  | { kind: 'delete'; item: DeletedCard }
  | { kind: 'complete_tree'; rootId: Id };

export type UndoCommand =
  | { kind: 'delete'; id: Id; version: number }
  | { kind: 'move'; id: Id; body: ItemMoveBody }
  | { kind: 'patch'; id: Id; body: ItemPatchBody }
  /**
   * `then` (Ruling T8-a): the state a re-created task must get back — a create always starts `not_started` and
   * unblocked, so a second PATCH with the version the create returned restores `status`/`blocked`/`blocker`.
   */
  | { kind: 'create'; body: ItemCreateBody; then?: UndoState };

/** The part of a task's assignment block a create cannot set (spec §4.3). */
export type UndoState = { status: TaskStatus; blocked: boolean; blocker: string | null };

export const UNDO_CONFLICT_MESSAGE = 'Không hoàn tác được — thẻ vừa được người khác sửa.';

const toRecipientInput = (r: Recipient): RecipientInput => ({ kind: 'user', userId: r.userId });

/** Where `id` sat in `prior`: its parent and the sibling right before it (null = first). */
function placementIn(prior: readonly BoardItem[], id: Id): ItemMoveBody | null {
  const item = prior.find((i) => i.id === id);
  if (item === undefined) return null;
  const siblings = prior.filter((i) => i.parentId === item.parentId).sort(comparePositions);
  const index = siblings.findIndex((i) => i.id === id);
  return { parentId: item.parentId, afterId: index === 0 ? null : siblings[index - 1]!.id };
}

/**
 * Spec §6.7: the inverse command the browser sends to undo the person's own action, computed from the board as it
 * was before (`prior`). Null when there is none: a confirmed complete-tree, a deleted card with comments or files
 * (they cannot come back with it), or an old value the caller did not supply.
 */
export function inverseFor(action: UndoAction, prior: readonly BoardItem[]): UndoCommand | null {
  switch (action.kind) {
    case 'complete_tree': return null;
    case 'create': return { kind: 'delete', id: action.id, version: action.version };
    case 'move': {
      const body = placementIn(prior, action.id);
      return body === null ? null : { kind: 'move', id: action.id, body };
    }
    case 'field': {
      const before = prior.find((i) => i.id === action.id);
      if (before === undefined) return null;
      const body: Record<string, unknown> = { version: action.version };
      for (const field of action.fields) {
        let value: unknown;
        if (action.previous !== undefined && field in action.previous) value = action.previous[field];
        else if (field === 'description' || field === 'strategyLink') return null;
        else if (field === 'title') value = before.title;
        else if (before.itemType !== 'task') return null;
        else value = field === 'recipient' ? toRecipientInput(before.recipient) : before[field as keyof BoardTaskCard];
        body[field] = value;
      }
      return { kind: 'patch', id: action.id, body: body as ItemPatchBody };
    }
    case 'status': {
      const before = prior.find((i) => i.id === action.id);
      if (before?.itemType !== 'task') return null;
      return {
        kind: 'patch', id: action.id,
        body: { version: action.version, status: before.status, blocked: before.blocked, blocker: before.blocker },
      };
    }
    case 'type': {
      const before = prior.find((i) => i.id === action.id);
      return before === undefined ? null
        : { kind: 'patch', id: action.id, body: { version: action.version, itemType: before.itemType } };
    }
    case 'delete': {
      const { item } = action;
      if (item.commentCount > 0 || item.fileCount > 0) return null;
      const place = placementIn(prior, item.id);
      if (place === null) return null;
      // A root comes back into the home it left (sharing D6): a standalone root revives its own audience, a project
      // root names its project again. A project the viewer cannot see, or one deleted since, would refuse the create.
      let home: { sharing: { revive: Id } } | { projectId: Id } | Record<string, never> = {};
      if (place.parentId === null) {
        const project = item.project ?? null;
        if (project === null) home = { sharing: { revive: item.id } };
        else if (project.restricted === true || project.deleted) return null;
        else home = { projectId: project.id };
      }
      const description = item.description ?? null;
      if (item.itemType === 'note') {
        return { kind: 'create', body: { ...place, ...home, itemType: 'note', title: item.title, description } };
      }
      const fresh = item.status === 'not_started' && !item.blocked && item.blocker === null;
      return {
        kind: 'create',
        body: {
          ...place, ...home, itemType: 'task', title: item.title, description, recipient: toRecipientInput(item.recipient),
          deadline: item.deadline, priority: item.priority,
          ...(item.strategyLink !== undefined ? { strategyLink: item.strategyLink } : {}),
          draftId: crypto.randomUUID(),
        },
        ...(fresh ? {} : { then: { status: item.status, blocked: item.blocked, blocker: item.blocker } }),
      };
    }
  }
}

export type UndoOutcome = { ok: true; result: unknown } | { ok: false; message: string };

/**
 * Sends one inverse command exactly once. A `VERSION_CONFLICT` means somebody changed the card since: the undo is
 * reported as impossible and never retried with the newer version, which would overwrite their change. A re-create
 * with `then` sends its state PATCH next, carrying the version the create returned; a refusal of that second step
 * is reported the same way, and the create is not sent again.
 */
export async function runUndo(command: UndoCommand, send: (command: UndoCommand) => Promise<unknown>): Promise<UndoOutcome> {
  try {
    let result = await send(command);
    if (command.kind === 'create' && command.then !== undefined) {
      const created = result as { id: Id; version: number };
      result = await send({ kind: 'patch', id: created.id, body: { version: created.version, ...command.then } });
    }
    return { ok: true, result };
  } catch (error) {
    if (error instanceof ApiClientError && error.code === 'VERSION_CONFLICT') return { ok: false, message: UNDO_CONFLICT_MESSAGE };
    return { ok: false, message: error instanceof ApiClientError ? error.message : 'Đã có lỗi xảy ra, vui lòng thử lại' };
  }
}

const OPTIMISTIC_FIELDS = ['title', 'status', 'blocked', 'blocker', 'deadline', 'priority', 'recipient'] as const;

/**
 * The board as an inverse command leaves it, for the optimistic cache (a refusal restores the snapshot): a delete
 * removes the card, a move puts it back, a PATCH writes the plain fields back. A re-create and a type change wait
 * for the server, which alone knows the new id and the woken block.
 */
export function undoPreview(items: readonly BoardItem[], command: UndoCommand): BoardItem[] {
  switch (command.kind) {
    case 'create': return [...items];
    case 'delete': return items.filter((i) => i.id !== command.id);
    case 'move': return items.some((i) => i.id === command.id) ? applyMove(items, command.id, command.body) : [...items];
    case 'patch': {
      const { body } = command;
      if (body.itemType !== undefined) return [...items];
      return items.map((card) => {
        if (card.id !== command.id) return card;
        const next: Record<string, unknown> = { ...card };
        for (const key of OPTIMISTIC_FIELDS) {
          if (body[key] !== undefined && (card.itemType === 'task' || key === 'title')) next[key] = body[key];
        }
        return next as BoardItem;
      });
    }
  }
}

/** Spec §6.2 Bảng: dropping a card on a column sets that status; "Đã xong" also clears the blocker. */
export function statusDropFields(card: BoardTaskCard, status: TaskStatus): Pick<ItemPatchBody, 'status' | 'blocked' | 'blocker'> {
  return status === 'completed' && (card.blocked || card.blocker !== null)
    ? { status, blocked: false, blocker: null } : { status };
}

// Remote merge ----------------------------------------------------------------------------------------------

/** The remote version of each held-back card; null when the remote board no longer has it. */
export type PendingRemote = ReadonlyMap<Id, BoardItem | null>;
export type MergeResult = { board: BoardDTO; deferredIds: Id[]; pending: PendingRemote };

/**
 * Spec §6.8: another person's changes land at once, except on a card this person is dragging or typing on
 * (`busyIds`) — that card keeps its local state until the gesture ends, so the text being typed is never
 * overwritten. The held-back remote versions are returned in `pending` for `releaseDeferred`.
 */
export function mergeRemote(local: BoardDTO, remote: BoardDTO, busyIds: ReadonlySet<Id>): MergeResult {
  if (busyIds.size === 0) return { board: remote, deferredIds: [], pending: new Map() };
  const localById = new Map(local.items.map((i) => [i.id, i]));
  const pending = new Map<Id, BoardItem | null>();
  const items: BoardItem[] = [];
  const seen = new Set<Id>();
  for (const item of remote.items) {
    seen.add(item.id);
    const mine = busyIds.has(item.id) ? localById.get(item.id) : undefined;
    if (mine === undefined) { items.push(item); continue; }
    items.push(mine);
    if (JSON.stringify(mine) !== JSON.stringify(item)) pending.set(item.id, item);
  }
  for (const id of busyIds) {
    const mine = localById.get(id);
    if (mine === undefined || seen.has(id)) continue;
    items.push(mine);
    pending.set(id, null);
  }
  return { board: { ...remote, items }, deferredIds: [...pending.keys()], pending };
}

/**
 * The gesture on `id` ended: its held-back remote version replaces the local card. `draft.title` is the title
 * this person is saving right now; it stays on screen until its own save answers.
 */
export function releaseDeferred(
  board: BoardDTO, pending: PendingRemote, id: Id, draft?: { title?: string },
): { board: BoardDTO; pending: PendingRemote } {
  if (!pending.has(id)) return { board, pending };
  const remote = pending.get(id)!;
  const rest = new Map(pending);
  rest.delete(id);
  const items = remote === null
    ? board.items.filter((i) => i.id !== id)
    : board.items.map((i) => (i.id !== id ? i : draft?.title !== undefined ? { ...remote, title: draft.title } : remote));
  return { board: { ...board, items }, pending: rest };
}

// Live sync (spec §5.5, §6.8) ---------------------------------------------------------------------------------

/** How long the stream may be gone before the page says so (spec §6.8 "quá 5 s"). */
export const LIVE_LOST_MS = 5000;

/** The fields that describe a data write on a card: a new version, a new place, or a new state. */
function sameWrite(a: BoardItem, b: BoardItem): boolean {
  return a.version === b.version && a.parentId === b.parentId && a.position === b.position
    && (a.itemType === 'task' ? a.status : null) === (b.itemType === 'task' ? b.status : null);
}

/** The cards of `after` that are new or were written since `before`, compared by id, version, parent, position, status. */
export function changedCards(before: readonly BoardItem[], after: readonly BoardItem[]): BoardItem[] {
  const old = new Map(before.map((card) => [card.id, card]));
  return after.filter((card) => {
    const was = old.get(card.id);
    return was === undefined || !sameWrite(was, card);
  });
}

/**
 * Which changed cards flash, and with whose name: the card's own `lastChangedBy`, when it is somebody else — or the
 * person's own Hana (AI S1 D204: "Bình (qua Hana)" flashes for Bình too; their own hand never does). The actor is
 * never guessed from presence — a person looking at a card is not necessarily the one who wrote it.
 */
export function peerFlashes(changed: readonly BoardItem[], me: Id): Map<Id, string> {
  const out = new Map<Id, string>();
  for (const card of changed) {
    const by = card.lastChangedBy;
    if (by !== null && (by.id !== me || by.via?.channel === 'assistant')) out.set(card.id, actorLabel(by.name, by.via));
  }
  return out;
}

/**
 * The one line over the board while assistants build (AI S1 spec §8.3.1, D201; S8 §6.2, D16), as segments to draw
 * (D41): "✦ Mây đang tạo 4 thẻ · An ✦ đang tạo 1 thẻ". The viewer's own sessions say the viewer's persona (`myPersona`) and come first; anyone else's
 * say "{name} ✦" — assisted, never whose persona (the server never sends it, and the viewer's is not theirs). It counts
 * only entries on a card this viewer sees (`visibleIds`; a masked entry has `itemId: null`) that carry a `count`; one
 * segment per owner (keyed on the person, never the name), its verbs joined by ", ". An owner goes by the given name, or
 * by the full name when another owner in the line, the viewer included, shares it. Null when nothing counts.
 */
export function hanaBand(
  entries: readonly AssistantPresence[], names: ReadonlyMap<Id, string>, visibleIds: { has: (id: Id) => boolean },
  me: Id, myPersona: string,
): AssistedSegment[] | null {
  const owners: { ownerId: Id; name: string; verbs: { verb: string; count: number }[] }[] = [];
  for (const entry of entries) {
    if (entry.itemId === null || !visibleIds.has(entry.itemId) || entry.count === undefined) continue;
    const name = names.get(entry.onBehalfOf);
    if (name === undefined) continue;
    const verb = entry.activity === 'building' ? 'tạo' : entry.activity === 'moving' ? 'chuyển'
      : entry.activity === 'editing' ? 'sửa' : null;
    if (verb === null) continue;
    let owner = owners.find((o) => o.ownerId === entry.onBehalfOf);
    if (owner === undefined) owners.push(owner = { ownerId: entry.onBehalfOf, name, verbs: [] });
    const same = owner.verbs.find((v) => v.verb === verb);
    if (same !== undefined) same.count += entry.count;
    else owner.verbs.push({ verb, count: entry.count });
  }
  if (owners.length === 0) return null;
  const ordered = [...owners.filter((o) => o.ownerId === me), ...owners.filter((o) => o.ownerId !== me)];
  const givenBy = new Map<string, Set<Id>>();
  for (const o of ordered) givenBy.set(givenName(o.name), (givenBy.get(givenName(o.name)) ?? new Set()).add(o.ownerId));
  const called = (o: (typeof owners)[number]) => (givenBy.get(givenName(o.name))!.size > 1 ? o.name.trim() : givenName(o.name));
  return ordered.map((o) => ({ name: o.ownerId === me ? myPersona : called(o), own: o.ownerId === me,
    text: ` đang ${o.verbs.map((v) => `${v.verb} ${v.count} thẻ`).join(', ')}` }));
}

/** The one line at the top of the board: which cards changed, the one "Xem" opens, and what it says. */
export type LiveBanner = { ids: Id[]; target: Id; text: string };
export type PeerChange = { id: Id; name: string; title: string };

/** Adds a batch of other people's changes to the banner; a burst stays one line, "Xem" opens the latest card. */
export function bannerFor(current: LiveBanner | null, changes: readonly PeerChange[]): LiveBanner | null {
  if (changes.length === 0) return current;
  const ids = [...(current?.ids ?? [])];
  for (const change of changes) if (!ids.includes(change.id)) ids.push(change.id);
  const last = changes.at(-1)!;
  const text = ids.length === 1 ? `${last.name} vừa đổi “${last.title}”` : `Có ${ids.length} thay đổi mới`;
  return { ids, target: last.id, text };
}

/**
 * One read at a time, and at most one waiting (spec §6.8): every signal that arrives while a read runs is folded
 * into a single further read, so a burst of changes costs two reads, never a pile of parallel ones.
 */
export function catchUpLoop(refreshBoardSinceCurrentRevision: () => Promise<void>): () => Promise<void> {
  let reading = false;
  let pending = false;
  return async function catchUp(): Promise<void> {
    if (reading) { pending = true; return; }
    reading = true;
    try {
      do {
        pending = false;
        await refreshBoardSinceCurrentRevision();
      } while (pending);
    } finally { reading = false; }
  };
}

// Map moves -------------------------------------------------------------------------------------------------

/**
 * Where a drag ended on a card: its centre makes a child, its top or bottom edge a sibling before or after (left or
 * right on a cell of a 2–3 column leaf grid, leaf grid §4.4). Data order either way.
 */
export type DropZone = 'child' | 'before' | 'after';

/** Spec §6.6: the move refusals the browser says itself, before sending (the server's words for the rest). */
export const MOVE_INTO_DONE = 'Việc lớn đó đã xong — mở lại nó trước khi chuyển việc đang mở vào';
export const MOVE_REFUSALS: Record<TreeViolation, string> = {
  CYCLE: 'Không kéo được thẻ vào nhánh của chính nó',
  NOTE_AT_ROOT: 'Ghi chú không được đứng ở gốc',
  TREE_DONE: MOVE_INTO_DONE,
  OPEN_CHILDREN: 'Cây còn việc đang mở',
  DELETED_PARENT: 'Thẻ cha đã bị xóa',
};

/** The live siblings under `parentId` in sibling order, without `except` (the card being moved). */
function siblingsOf(items: readonly BoardItem[], parentId: Id | null, except: Id): BoardItem[] {
  return items.filter((i) => i.parentId === parentId && i.id !== except).sort(comparePositions);
}

/**
 * The move body for dropping `id` on `targetId` (spec §6.2 Kéo thả). An edge of a root places the card beside
 * that root, at the top level: a detach. Roots are ordered by position among all roots, as the server orders them.
 */
export function dropPlacement(items: readonly BoardItem[], id: Id, targetId: Id, zone: DropZone): ItemMoveBody {
  if (zone === 'child') return { parentId: targetId };
  const target = items.find((i) => i.id === targetId);
  if (target === undefined) throw new RangeError(`unknown card ${targetId}`);
  if (zone === 'after') return { parentId: target.parentId, afterId: targetId };
  const siblings = siblingsOf(items, target.parentId, id);
  const at = siblings.findIndex((i) => i.id === targetId);
  return { parentId: target.parentId, afterId: at <= 0 ? null : siblings[at - 1]!.id };
}

export type MoveCheck = { kind: 'ok' } | { kind: 'noop' } | { kind: 'refused'; code: TreeViolation; message: string };

/** The sibling a placement puts the card after: null = first; an absent or stale `afterId` appends. */
function previousFor(siblings: readonly BoardItem[], body: ItemMoveBody): Id | null {
  if (body.afterId === null) return null;
  if (body.afterId !== undefined && siblings.some((i) => i.id === body.afterId)) return body.afterId;
  return siblings.at(-1)?.id ?? null;
}

/**
 * The browser's preflight for one move (spec §5.4: CYCLE, NOTE_AT_ROOT and TREE_DONE are refused before
 * sending; the server refuses them again). An exact no-op — the same parent and the same previous sibling — is
 * reported so nothing is sent at all.
 */
export function checkMove(items: readonly BoardItem[], id: Id, body: ItemMoveBody): MoveCheck {
  const card = items.find((i) => i.id === id);
  if (card === undefined) throw new RangeError(`unknown card ${id}`);
  if (card.parentId === body.parentId) {
    const all = items.filter((i) => i.parentId === card.parentId).sort(comparePositions);
    const at = all.findIndex((i) => i.id === id);
    const current = at === 0 ? null : all[at - 1]!.id;
    if (previousFor(siblingsOf(items, body.parentId, id), body) === current) return { kind: 'noop' };
  }
  const tree = items.map((i) => ({
    id: i.id, parentId: i.parentId, itemType: i.itemType, status: i.itemType === 'task' ? i.status : null, deletedAt: null,
  }));
  // A drop on the edge of the card's own child would make it its own parent: to the person that is the same
  // "into its own branch" refusal (the server answers DELETED_PARENT for a self-parent, Ruling T1-a).
  const code = body.parentId === id ? 'CYCLE' : validatePlacement(tree, id, body.parentId);
  return code === null ? { kind: 'ok' } : { kind: 'refused', code, message: MOVE_REFUSALS[code] };
}

/** The board as the move leaves it, for the optimistic cache: the new parent, and a position between the neighbours. */
export function applyMove(items: readonly BoardItem[], id: Id, body: ItemMoveBody): BoardItem[] {
  const siblings = siblingsOf(items, body.parentId, id);
  const previous = previousFor(siblings, body);
  const at = previous === null ? -1 : siblings.findIndex((i) => i.id === previous);
  const { position } = positionBetween(
    previous === null ? null : siblings[at]!.position, siblings[at + 1]?.position ?? null,
  );
  return items.map((i) => (i.id === id ? { ...i, parentId: body.parentId, position } : i));
}

// Per-user storage ------------------------------------------------------------------------------------------

/** The three board views, then S9's two delegation lists (§8.3, L3 D87: `?view=delegated`, `?view=assigned`). */
export type WorkplaceView = 'map' | 'kanban' | 'list' | 'delegated' | 'assigned';
const VIEWS: readonly WorkplaceView[] = ['map', 'kanban', 'list', 'delegated', 'assigned'];
/** Each view as the page's view switch names it (and Hana's status line, P2 review F19). */
export const WORKPLACE_VIEW_LABEL: Readonly<Record<WorkplaceView, string>> = {
  map: 'Bản đồ', kanban: 'Bảng', list: 'Danh sách', delegated: 'Tôi đã giao', assigned: 'Nhờ tôi',
};
/** "Tôi đã giao" and "Nhờ tôi": per-person lists, not board views — no chips, no notes, not on a project page (D88, D89). */
export const isDelegationView = (view: WorkplaceView): view is 'delegated' | 'assigned' =>
  view === 'delegated' || view === 'assigned';
/** The view a Workplace URL names in `?view=`, or null when it names none (or not a view). */
export function workplaceViewOf(search: string): WorkplaceView | null {
  const value = new URLSearchParams(search).get('view');
  return VIEWS.includes(value as WorkplaceView) ? value as WorkplaceView : null;
}

export const viewKey = (userId: Id): string => `workplace:view:${userId}`;
export { collapsedKey };

/** `localStorage` itself may throw (blocked site data); every access goes through here. */
function storageOf(storage: Storage | undefined): Storage | null {
  try {
    return storage ?? globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/** The view this person stored on this browser, or `null` when there is none (or it is not a view). */
export function readStoredView(userId: Id, storage?: Storage): WorkplaceView | null {
  try {
    const value = storageOf(storage)?.getItem(viewKey(userId));
    return VIEWS.includes(value as WorkplaceView) ? value as WorkplaceView : null;
  } catch {
    return null;
  }
}

/** The view used last by this person on this browser; the map on first visit (spec §6.1). */
export function readLastView(userId: Id, storage?: Storage): WorkplaceView {
  return readStoredView(userId, storage) ?? 'map';
}

export function writeLastView(userId: Id, view: WorkplaceView, storage?: Storage): void {
  try {
    storageOf(storage)?.setItem(viewKey(userId), view);
  } catch {
    // A refused write only costs the remembered view.
  }
}

export function readCollapsed(userId: Id, storage?: Storage): Set<Id> {
  try {
    const raw = storageOf(storage)?.getItem(collapsedKey(userId));
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((v): v is Id => typeof v === 'string') : []);
  } catch {
    return new Set();
  }
}

export function writeCollapsed(userId: Id, collapsed: ReadonlySet<Id>, storage?: Storage): void {
  try {
    storageOf(storage)?.setItem(collapsedKey(userId), JSON.stringify([...collapsed]));
  } catch {
    // A refused write only costs the remembered folds.
  }
}
